import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ToasterService } from './toaster.service';

export interface ToasterConfig {
    message: string;
    status: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
}

@Component({
    selector: 'app-toaster',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div
            *ngIf="toasterService.isVisible()"
            animate.enter="anim-toast-enter"
            animate.leave="anim-toast-leave"
            class="fixed right-5 top-5 z-[99999] w-[min(400px,calc(100vw-2.5rem))]"
            role="alert"
            aria-live="assertive"
        >
            <div
                class="relative overflow-hidden rounded-xl border backdrop-blur-sm shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
                [ngClass]="containerClasses"
            >
                <div class="flex items-start gap-3 p-4">
                    <!-- Icon with colored background circle -->
                    <div
                        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                        [ngClass]="iconBgClasses"
                    >
                        <svg
                            *ngIf="toasterService.toast()?.status === 'success'"
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                        <svg
                            *ngIf="toasterService.toast()?.status === 'error'"
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                        <svg
                            *ngIf="toasterService.toast()?.status === 'warning'"
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        <svg
                            *ngIf="toasterService.toast()?.status === 'info'"
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>

                    <!-- Content -->
                    <div class="min-w-0 flex-1 pt-0.5">
                        <p
                            class="break-words text-sm font-medium leading-snug"
                            [ngClass]="textClasses"
                            style="overflow-wrap: anywhere; word-break: break-word;"
                        >
                            {{ toasterService.toast()?.message }}
                        </p>
                    </div>

                    <!-- Close Button -->
                    <button
                        (click)="closeToast()"
                        class="ml-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1"
                        [ngClass]="closeBtnClasses"
                        aria-label="Dismiss notification"
                    >
                        <svg
                            class="h-3.5 w-3.5"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <!-- Accent stripe at the left edge -->
                <div
                    class="absolute left-0 top-0 h-full w-1 rounded-l-xl"
                    [ngClass]="stripeClasses"
                ></div>
            </div>
        </div>
    `,
    host: {
        class: 'block',
    },
})
export class ToasterComponent {
    protected toasterService = inject(ToasterService);

    get containerClasses(): string {
        switch (this.toasterService.toast()?.status) {
            case 'success':
                return 'bg-white/95 border-green-200';
            case 'error':
                return 'bg-white/95 border-red-200';
            case 'warning':
                return 'bg-white/95 border-amber-200';
            case 'info':
                return 'bg-white/95 border-blue-200';
            default:
                return 'bg-white/95 border-gray-200';
        }
    }

    get iconBgClasses(): string {
        switch (this.toasterService.toast()?.status) {
            case 'success':
                return 'bg-green-100 text-green-600';
            case 'error':
                return 'bg-red-100 text-red-600';
            case 'warning':
                return 'bg-amber-100 text-amber-600';
            case 'info':
                return 'bg-blue-100 text-blue-600';
            default:
                return 'bg-gray-100 text-gray-600';
        }
    }

    get textClasses(): string {
        switch (this.toasterService.toast()?.status) {
            case 'success':
                return 'text-green-800';
            case 'error':
                return 'text-red-800';
            case 'warning':
                return 'text-amber-800';
            case 'info':
                return 'text-blue-800';
            default:
                return 'text-gray-800';
        }
    }

    get closeBtnClasses(): string {
        switch (this.toasterService.toast()?.status) {
            case 'success':
                return 'text-green-400 hover:text-green-600 hover:bg-green-50 focus:ring-green-400';
            case 'error':
                return 'text-red-400 hover:text-red-600 hover:bg-red-50 focus:ring-red-400';
            case 'warning':
                return 'text-amber-400 hover:text-amber-600 hover:bg-amber-50 focus:ring-amber-400';
            case 'info':
                return 'text-blue-400 hover:text-blue-600 hover:bg-blue-50 focus:ring-blue-400';
            default:
                return 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 focus:ring-gray-400';
        }
    }

    get stripeClasses(): string {
        switch (this.toasterService.toast()?.status) {
            case 'success':
                return 'bg-green-500';
            case 'error':
                return 'bg-red-500';
            case 'warning':
                return 'bg-amber-500';
            case 'info':
                return 'bg-blue-500';
            default:
                return 'bg-gray-500';
        }
    }

    closeToast() {
        this.toasterService.hide();
    }
}
